import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { fmtDay, todayISO } from "../lib/dates";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Stamp, Wallet, ClipboardPlus } from "lucide-react";
import { toast } from "sonner";

const GROUPS = (t) => [
  ["overdue", "Overdue", "bg-rose-600 text-white"],
  ["today", "Due today", "bg-orange-500 text-white"],
  ["soon", `Within ${t[0]} days`, "bg-amber-100 text-amber-900 border border-amber-300"],
  ["later", `Within ${t[1]} days`, "bg-sky-50 text-sky-900 border border-sky-200"],
  ["missing", "Missing date — needs review", "bg-slate-100 text-slate-700 border border-slate-300"],
];
const left = d => d === null || d === undefined ? "Not set" : d < 0 ? `${-d} day${d === -1 ? "" : "s"} overdue` : d === 0 ? "Today" : `${d} day${d === 1 ? "" : "s"} left`;

export default function DeadlineBoxes() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/b2b/deadlines").then(r => setData(r.data)).catch(() => setData({ error: true })); }, []);
  if (data?.error) return <div data-testid="deadline-boxes-error" className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">Deadline boxes could not load. Please refresh; if this continues, contact your admin.</div>;
  const boxes = [
    data?.dashboard?.show_permit_box !== false && ["permit", "Visa / Permit Expiry", Stamp, data?.permit_days],
    data?.dashboard?.show_fee_box !== false && ["fee", "Fee Payment Deadlines", Wallet, data?.fee_days],
  ].filter(Boolean);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {boxes.map(([k, title, Icon, t]) => (
        <div key={k} data-testid={`deadline-box-${k}`} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 heading-font font-semibold text-slate-900"><Icon className="w-4 h-4 text-orange-600" /> {title}</div>
            <span className="text-[11px] text-slate-500">{data ? `Today ${fmtDay(data.today)} · ${data.timezone}` : "Loading…"}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {GROUPS(t || [7, 30]).map(([g, label, tone]) => (
              <button key={g} data-testid={`deadline-${k}-${g}`} disabled={!data} onClick={() => setOpen({ k, g, title, label })}
                className={`rounded-lg px-3 py-2 text-left transition-transform hover:-translate-y-0.5 ${tone}`}>
                <div className="heading-font text-xl font-bold">{data ? data[k][g].length : "…"}</div>
                <div className="text-[11px] leading-tight">{label}</div>
              </button>
            ))}
          </div>
        </div>
      ))}
      {open && <DeadlineList data={data} open={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

function DeadlineList({ data, open, onClose }) {
  const nav = useNavigate();
  const [g, setG] = useState(open.g);
  const rows = data[open.k][g];
  const task = async (r) => {
    try {
      await api.post("/tasks", { application_id: r.id, title: `Follow up: ${open.k === "permit" ? "permit expiry" : "fee payment"} — ${r.student_name}`,
        due_date: todayISO(), priority: (r.days_left ?? 99) <= 0 ? "High" : "Medium" });
      toast.success("Follow-up task created");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create task"); }
  };
  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent data-testid="deadline-list-dialog" className="bg-white max-w-4xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="heading-font">{open.title}</DialogTitle><DialogDescription>Nearest or most overdue first.</DialogDescription></DialogHeader>
        <div className="flex flex-wrap gap-1.5">
          {GROUPS(data[`${open.k}_days`]).map(([k, label]) => (
            <button key={k} data-testid={`deadline-tab-${k}`} onClick={() => setG(k)} className={`rounded-full border px-3 py-1 text-xs ${g === k ? "bg-orange-500 border-orange-500 text-white" : "border-slate-200"}`}>{label} <b>{data[open.k][k].length}</b></button>
          ))}
        </div>
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>{(open.k === "permit" ? ["Student", "Document", "Expiry", "Days left", "Officer", ""] : ["Student", "App ID", "College", "Course", "Due", "Amount", "Status", "Officer", ""]).map(h => <th key={h} className="text-left px-3 py-2">{h}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} data-testid={`deadline-row-${r.id}`} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-medium">{r.student_name}</td>
                  {open.k === "permit" ? <>
                    <td className="px-3 py-2">{r.doc_type}{r.applications > 1 && <span className="text-xs text-slate-500"> · {r.applications} apps</span>}</td>
                    <td className="px-3 py-2">{fmtDay(r.expiry)}</td>
                  </> : <>
                    <td className="px-3 py-2 mono text-xs">{r.application_id}</td>
                    <td className="px-3 py-2">{r.college}</td>
                    <td className="px-3 py-2 max-w-[180px] truncate" title={r.course}>{r.course || "—"}</td>
                    <td className="px-3 py-2">{fmtDay(r.due)}</td>
                    <td className="px-3 py-2">{r.amount ? `CA$${r.amount}` : "—"}</td>
                    <td className="px-3 py-2">{r.status}</td>
                  </>}
                  {open.k === "permit" && <td className="px-3 py-2 text-xs">{left(r.days_left)}</td>}
                  <td className="px-3 py-2 text-xs">{r.officer}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <Button size="sm" variant="ghost" data-testid={`deadline-open-${r.id}`} onClick={() => nav(r.student_id && open.k === "permit" ? `/students/${r.student_id}` : `/applications/${r.id}`)}>Open</Button>
                    <Button size="sm" variant="ghost" data-testid={`deadline-task-${r.id}`} onClick={() => task(r)}><ClipboardPlus className="w-4 h-4" /></Button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-500">Nothing in this group.</td></tr>}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
